import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  createStyles,
  Divider,
  FileInput,
  Flex,
  Grid,
  Loader,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  SimpleGrid,
  Paper,
  Group,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";
import { useSelector } from "react-redux";
import moment from "moment";
import swal from "sweetalert";
import { IconChevronDown, IconChevronRight, IconClipboardCheck, IconEdit, IconFolderPlus, IconPlus, IconShieldCheck, IconTrash, IconPill, IconChecklist, IconFileAnalytics } from "@tabler/icons-react";
import RecordNotFound from "../../../../components/RecordNotFound";
import CustomBillingPagination from "../../../../components/Pagination/CustomBillingPagination";
import {
  getComplianceAlerts,
  getComplianceSettings,
  updateComplianceSettings,
  getDocumentTemplates,
  toggleDocumentTemplate,
  toggleDocumentFolder,
  createCustomFolder,
  addCustomItem,
  deleteCustomFolder,
  deleteCustomItem,
  addCredentialType,
  addFacilityComplianceItem,
  addClientComplianceItem,
  getClientComplianceItems,
  updateClientComplianceItem,
  deleteClientComplianceItem,
} from "../../../../services/compliance";
import { uploadFileToS3Api } from "../../../../utils/PresignedUrlFunction";
import { getAllNoteTypes } from "../../../../services/note";
import { getTaskTypes } from "../../../../services/task";

const useStyles = createStyles(() => ({
  wrapper: {
    marginTop: 40,
    marginBottom: 40,
  },
  statusBadge: {
    textTransform: "capitalize",
    fontFamily: "Inter",
    fontWeight: 500,
  },
  settingsCard: {
    background: "#fff",
    margin: "0",
    borderRadius: "12px",
    padding: "12px 26px",
  },
  settingsTitle: {
    color: "#262626",
    textAlign: "left",
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  settingsDescription: {
    color: "#737373",
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    marginBottom: 8,
  },
  settingsBtnGroup: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    margin: "1rem 0",
  },
  table: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: "12px",
    padding: "0 10px",
    background: "#FFFFFF",
    border: "1px solid #E5E5E5",
    marginTop: "10px",
  },
  title: {
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
  },
  folderHeader: {
    cursor: "pointer",
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
    background: "#FAFAFA",
    marginBottom: "8px",
    "&:hover": {
      background: "#F0F0F0",
    },
  },
  templateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px 10px 32px",
    borderBottom: "1px solid #F0F0F0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  templateName: {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    color: "#404040",
  },
  sectionCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 600,
    color: "#262626",
  },
  sectionBadge: {
    fontFamily: "Inter",
    fontWeight: 500,
  },
}));

const statusColor = (status) => {
  switch (status) {
    case "compliant": return "green";
    case "expiring": return "yellow";
    case "expired": return "red";
    case "missing": return "gray";
    case "non_compliant": return "red";
    default: return "gray";
  }
};

const CATEGORY_LABELS = {
  staff: { label: "Staff Documents", color: "blue" },
  facility: { label: "Facility Documents", color: "violet" },
  client: { label: "Client Documents", color: "teal" },
};

const ComplianceTab = ({ embeddedSubTab, embeddedDocCategory, wizardHighlight } = {}) => {
  const { classes } = useStyles();
  const { state: locationState } = useLocation();
  // If the user was navigated here from the Command Center setup wizard
  // with an explicit sub-tab target, open that tab on mount. When rendered
  // inline inside the setup wizard, `embeddedSubTab` overrides both the
  // location state and the visible tab bar.
  const initialSubTab = ["documents", "charting", "settings"].includes(embeddedSubTab)
    ? embeddedSubTab
    : ["documents", "charting", "settings"].includes(locationState?.complianceSubTab)
    ? locationState.complianceSubTab
    : "documents";
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab);
  useEffect(() => {
    if (embeddedSubTab && ["documents", "charting", "settings"].includes(embeddedSubTab)) {
      setActiveSubTab(embeddedSubTab);
      return;
    }
    if (locationState?.complianceSubTab && ["documents", "charting", "settings"].includes(locationState.complianceSubTab)) {
      setActiveSubTab(locationState.complianceSubTab);
    }
  }, [locationState?.complianceSubTab, embeddedSubTab]);
  const [currentPage, setCurrentPage] = useState(1);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [togglingTemplate, setTogglingTemplate] = useState(null);

  const compliance = useSelector((state) => state.compliance) || {};
  const alerts = compliance.alerts || [];
  const alertsCount = compliance.alertsCount || 0;
  const settings = compliance.settings || null;
  const loading = compliance.loading || false;
  const addLoading = compliance.addLoading || false;
  const documentTemplates = compliance.documentTemplates || null;
  const clientItems = Array.isArray(compliance.clientItems) ? compliance.clientItems : [];

  // Charting rules = client items with category notes/task/mar
  const chartingRules = clientItems.filter((ci) => ci && ["notes", "task", "mar"].includes(ci.category));

  // Charting rule modal state
  const [chartRuleModalOpen, setChartRuleModalOpen] = useState(false);
  const [editingChartRule, setEditingChartRule] = useState(null);
  const [chartRuleName, setChartRuleName] = useState("");
  const [chartRuleDescription, setChartRuleDescription] = useState("");
  const [chartRuleCategory, setChartRuleCategory] = useState("notes");
  const [chartRuleThreshold, setChartRuleThreshold] = useState(100);
  const [chartRuleNoteType, setChartRuleNoteType] = useState(null);
  const [chartRuleTaskType, setChartRuleTaskType] = useState(null);
  const [chartRuleSubmitting, setChartRuleSubmitting] = useState(false);
  const [chartRuleFrequency, setChartRuleFrequency] = useState("daily");
  const [chartRuleDaysOfWeek, setChartRuleDaysOfWeek] = useState([]);
  const [chartRuleDaysOfMonth, setChartRuleDaysOfMonth] = useState([]);

  // Default rule schedule modal
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalType, setScheduleModalType] = useState(null); // "note" or "task"
  const [scheduleModalFreq, setScheduleModalFreq] = useState("daily");
  const [scheduleModalDaysOfWeek, setScheduleModalDaysOfWeek] = useState([]);
  const [scheduleModalDaysOfMonth, setScheduleModalDaysOfMonth] = useState([]);
  const [scheduleModalSaving, setScheduleModalSaving] = useState(false);

  const openScheduleModal = (type) => {
    const schedule = type === "note" ? (settings?.defaultNoteSchedule || {}) : type === "task" ? (settings?.defaultTaskSchedule || {}) : (settings?.defaultMarSchedule || {});
    setScheduleModalType(type);
    setScheduleModalFreq(schedule.frequency || "daily");
    setScheduleModalDaysOfWeek(schedule.daysOfWeek || []);
    setScheduleModalDaysOfMonth(schedule.daysOfMonth || []);
    setScheduleModalOpen(true);
  };

  const handleScheduleModalSave = async () => {
    setScheduleModalSaving(true);
    const field = scheduleModalType === "note" ? "defaultNoteSchedule" : scheduleModalType === "task" ? "defaultTaskSchedule" : "defaultMarSchedule";
    await updateComplianceSettings({
      [field]: {
        frequency: scheduleModalFreq,
        daysOfWeek: scheduleModalFreq === "weekly" ? scheduleModalDaysOfWeek : [],
        daysOfMonth: scheduleModalFreq === "monthly" ? scheduleModalDaysOfMonth : [],
      },
    });
    await getComplianceSettings();
    setScheduleModalSaving(false);
    setScheduleModalOpen(false);
  };

  // Note types and task types from Redux
  const noteState = useSelector((state) => state.note) || {};
  const rawNoteTypes = noteState.allNotesTypes;
  const noteTypes = Array.isArray(rawNoteTypes) ? rawNoteTypes : Array.isArray(rawNoteTypes?.ntypes) ? rawNoteTypes.ntypes : [];
  const taskState = useSelector((state) => state.task) || {};
  const taskTypes = Array.isArray(taskState.taskTypes) ? taskState.taskTypes : [];

  const resetChartRuleForm = () => {
    setChartRuleName("");
    setChartRuleDescription("");
    setChartRuleCategory("notes");
    setChartRuleThreshold(100);
    setChartRuleNoteType(null);
    setChartRuleTaskType(null);
    setChartRuleFrequency("daily");
    setChartRuleDaysOfWeek([]);
    setChartRuleDaysOfMonth([]);
    setEditingChartRule(null);
  };

  const openChartRuleModal = (rule = null) => {
    if (rule) {
      setEditingChartRule(rule);
      setChartRuleName(rule.name || "");
      setChartRuleDescription(rule.description || "");
      setChartRuleCategory(rule.category || "notes");
      setChartRuleThreshold(rule.threshold ?? 100);
      setChartRuleNoteType(rule.noteType?._id || rule.noteType?.id || (typeof rule.noteType === "string" ? rule.noteType : null));
      setChartRuleTaskType(rule.taskType?._id || rule.taskType?.id || (typeof rule.taskType === "string" ? rule.taskType : null));
      setChartRuleFrequency(rule.schedule?.frequency || "daily");
      setChartRuleDaysOfWeek(rule.schedule?.daysOfWeek || []);
      setChartRuleDaysOfMonth(rule.schedule?.daysOfMonth || []);
    } else {
      resetChartRuleForm();
    }
    setChartRuleModalOpen(true);
  };

  const handleChartRuleSubmit = async () => {
    if (!chartRuleName.trim()) return swal("Error", "Name is required", "error");
    setChartRuleSubmitting(true);
    const payload = {
      name: chartRuleName.trim(),
      description: chartRuleDescription.trim(),
      category: chartRuleCategory,
      threshold: chartRuleThreshold,
      schedule: {
        frequency: chartRuleFrequency,
        daysOfWeek: chartRuleFrequency === "weekly" ? chartRuleDaysOfWeek : [],
        daysOfMonth: chartRuleFrequency === "monthly" ? chartRuleDaysOfMonth : [],
      },
    };
    if (chartRuleCategory === "notes" && chartRuleNoteType) payload.noteType = typeof chartRuleNoteType === "object" ? (chartRuleNoteType._id || chartRuleNoteType.id) : chartRuleNoteType;
    if (chartRuleCategory === "task" && chartRuleTaskType) payload.taskType = typeof chartRuleTaskType === "object" ? (chartRuleTaskType._id || chartRuleTaskType.id) : chartRuleTaskType;

    let success;
    if (editingChartRule) {
      success = await updateClientComplianceItem(editingChartRule._id || editingChartRule.id, payload);
    } else {
      success = await addClientComplianceItem(payload);
    }
    setChartRuleSubmitting(false);
    if (success) {
      resetChartRuleForm();
      setChartRuleModalOpen(false);
      getClientComplianceItems();
    }
  };

  const handleDeleteChartRule = async (id) => {
    const confirm = await swal({
      title: "Delete this charting rule?",
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (confirm) {
      await deleteClientComplianceItem(id);
      getClientComplianceItems();
    }
  };

  useEffect(() => {
    getDocumentTemplates();
    getComplianceSettings();
    getClientComplianceItems();
    getAllNoteTypes(false);
    getTaskTypes("all", false);
  }, []);

  useEffect(() => {
    if (activeSubTab === "alerts") {
      getComplianceAlerts(currentPage);
    } else if (activeSubTab === "documents") {
      getDocumentTemplates();
    } else if (activeSubTab === "charting") {
      getClientComplianceItems();
    }
  }, [activeSubTab, currentPage]);

  const WEIGHT_PRESETS = {
    assisted_living: { label: "Assisted Living", description: "Balanced MAR and Task tracking for assisted living facilities", marWeight: 50, taskWeight: 50, notesWeight: 0 },
    idd_group_home: { label: "IDD Group Home", description: "Balanced MAR and Notes tracking for IDD group home settings", marWeight: 50, taskWeight: 0, notesWeight: 50 },
  };

  useEffect(() => {
    if (settings && !settingsForm) {
      const preset = settings.scoreWeightPreset || "custom";
      const isKnownPreset = WEIGHT_PRESETS[preset];
      const notesW = settings.notesWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].notesWeight : 0);
      const marW = settings.marWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].marWeight : 50);
      const taskW = settings.taskWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].taskWeight : 50);
      const validWeights = notesW + marW + taskW === 100;
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        scoreWeightPreset: validWeights ? (isKnownPreset ? preset : "custom") : "custom",
        notesWeight: validWeights ? notesW : 0,
        marWeight: validWeights ? marW : 50,
        taskWeight: validWeights ? taskW : 50,
        commandCenterScoringEnabled: settings.commandCenterScoringEnabled !== false,
      });
    }
  }, [settings]);

  const handleSettingsChange = (field, value) => {
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    const totalWeight = (settingsForm.notesWeight || 0) +
      (settingsForm.marWeight || 0) + (settingsForm.taskWeight || 0);
    if (totalWeight !== 100) {
      const swal = (await import("sweetalert")).default;
      swal({ title: "Score weights must add up to 100%", text: `Current total: ${totalWeight}%`, icon: "warning" });
      return;
    }
    setSavingSettings(true);
    await updateComplianceSettings(settingsForm);
    setSavingSettings(false);
  };

  const handleResetSettings = () => {
    if (settings) {
      const preset = settings.scoreWeightPreset || "custom";
      const isKnownPreset = WEIGHT_PRESETS[preset];
      const notesW = settings.notesWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].notesWeight : 0);
      const marW = settings.marWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].marWeight : 50);
      const taskW = settings.taskWeight ?? (isKnownPreset ? WEIGHT_PRESETS[preset].taskWeight : 50);
      const validWeights = notesW + marW + taskW === 100;
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        scoreWeightPreset: validWeights ? (isKnownPreset ? preset : "custom") : "custom",
        notesWeight: validWeights ? notesW : 0,
        marWeight: validWeights ? marW : 50,
        taskWeight: validWeights ? taskW : 50,
        commandCenterScoringEnabled: settings.commandCenterScoringEnabled !== false,
      });
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleFolder = (key) => {
    setExpandedFolders((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleTemplate = async (category, folder, templateName, currentActive) => {
    const key = `${category}-${folder}-${templateName}`;
    setTogglingTemplate(key);
    const success = await toggleDocumentTemplate(category, folder, templateName, !currentActive);
    if (success) {
      await getDocumentTemplates();
    }
    setTogglingTemplate(null);
  };

  // ── Folder toggle (active/inactive) ──
  const [togglingFolder, setTogglingFolder] = useState(null);
  const handleToggleFolder = async (category, folder, currentActive) => {
    const key = `${category}-${folder}`;
    setTogglingFolder(key);
    const success = await toggleDocumentFolder(category, folder, !currentActive);
    if (success) {
      await getDocumentTemplates();
    }
    setTogglingFolder(null);
  };

  // ── Add Folder inline state ──
  const [addingFolderFor, setAddingFolderFor] = useState(null); // e.g. "staff"
  const [newFolderName, setNewFolderName] = useState("");
  const [addingFolderLoading, setAddingFolderLoading] = useState(false);
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !addingFolderFor) return;
    setAddingFolderLoading(true);
    const success = await createCustomFolder(addingFolderFor, newFolderName.trim());
    if (success) {
      await getDocumentTemplates();
      setNewFolderName("");
      setAddingFolderFor(null);
    }
    setAddingFolderLoading(false);
  };

  // ── Add Item inline state ──
  const [addingItemFor, setAddingItemFor] = useState(null); // e.g. "staff|Health Information"
  const [newItemName, setNewItemName] = useState("");
  const [addingItemLoading, setAddingItemLoading] = useState(false);
  const handleCreateItem = async () => {
    if (!newItemName.trim() || !addingItemFor) return;
    const [category, folder] = addingItemFor.split("|");
    setAddingItemLoading(true);
    const success = await addCustomItem(category, folder, newItemName.trim());
    if (success) {
      await getDocumentTemplates();
      setNewItemName("");
      setAddingItemFor(null);
    }
    setAddingItemLoading(false);
  };

  const handleDeleteFolder = async (category, folder) => {
    const confirmed = await swal({
      title: "Delete Folder?",
      text: `This will permanently remove the "${folder}" folder and all its custom templates. This cannot be undone.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;
    const success = await deleteCustomFolder(category, folder);
    if (success) {
      await getDocumentTemplates();
    }
  };

  const handleDeleteItem = async (category, folder, templateName) => {
    const confirmed = await swal({
      title: "Delete Template?",
      text: `This will permanently remove "${templateName}" from ${folder}. This cannot be undone.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;
    const success = await deleteCustomItem(category, folder, templateName);
    if (success) {
      await getDocumentTemplates();
    }
  };

  const renderAlerts = () => (
    <Box>
      <Text className={classes.title}>Compliance Alerts</Text>
      <Box className={classes.table}>
        <ScrollArea>
          <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: 120 }}>Category</th>
                <th style={{ minWidth: 200 }}>Item</th>
                <th style={{ minWidth: 120 }}>Status</th>
                <th style={{ minWidth: 150 }}>Expiration</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
              ) : alerts.length > 0 ? (
                alerts.map((a, i) => (
                  <tr key={i}>
                    <td><Badge variant="light" color={a.alertCategory === "staff" ? "blue" : a.alertCategory === "client" ? "teal" : "violet"}>{a.alertCategory}</Badge></td>
                    <td>{a.credentialType?.name || a.complianceItem?.name || "\u2014"}</td>
                    <td><Badge color={statusColor(a.status)} className={classes.statusBadge}>{a.status}</Badge></td>
                    <td>{a.expirationDate ? moment(a.expirationDate).format("MMM DD, YYYY") : "\u2014"}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4}><RecordNotFound /></td></tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <CustomBillingPagination
          data={alertsCount}
          itemsPerPage={20}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      </Box>
    </Box>
  );

  const renderSettings = () => {
    if (!settingsForm) {
      return <Flex align="center" justify="center" py={60}><Loader color="#FFAF4A" /></Flex>;
    }

    const totalWeight = (settingsForm.notesWeight || 0) +
      (settingsForm.marWeight || 0) + (settingsForm.taskWeight || 0);
    const activePreset = settingsForm.scoreWeightPreset || "custom";

    const handlePresetSelect = (presetKey) => {
      if (presetKey === "custom") {
        setSettingsForm((prev) => ({ ...prev, scoreWeightPreset: "custom" }));
        return;
      }
      const preset = WEIGHT_PRESETS[presetKey];
      if (preset) {
        setSettingsForm((prev) => ({
          ...prev,
          scoreWeightPreset: presetKey,
          marWeight: preset.marWeight,
          taskWeight: preset.taskWeight,
          notesWeight: preset.notesWeight,
        }));
      }
    };

    const handleWeightEdit = (field, value) => {
      setSettingsForm((prev) => ({
        ...prev,
        [field]: value,
        scoreWeightPreset: "custom",
      }));
    };

    const presetOptions = [
      ...Object.entries(WEIGHT_PRESETS).map(([key, p]) => ({ key, label: p.label })),
      { key: "custom", label: "Custom" },
    ];

    const scoringEnabled = settingsForm.commandCenterScoringEnabled !== false;

    const masterToggleCard = (
      <Box className={classes.settingsCard} mb="md">
        <Flex justify="space-between" align="center">
          <Box style={{ flex: 1, paddingRight: 24 }}>
            <Text className={classes.settingsTitle}>Command Center Scoring</Text>
            <Text className={classes.settingsDescription} mt={4}>
              Master toggle for all Command Center scoring. When off, scores in the
              Overview, Compliance, Operations, and Finance tabs are hidden and those
              features are disabled. The Action Center stays available for pending
              actions and expired documents, but scoring-based alerts (missing notes,
              tasks, MAR) are disabled.
            </Text>
          </Box>
          <Switch
            size="lg"
            checked={scoringEnabled}
            onChange={async () => {
              const next = !scoringEnabled;
              // Optimistically update local settingsForm so the UI flips
              // immediately — the useEffect that seeds settingsForm only
              // runs on first load, so we must mirror the change here.
              setSettingsForm((prev) => ({ ...prev, commandCenterScoringEnabled: next }));
              await updateComplianceSettings({ commandCenterScoringEnabled: next });
              getComplianceSettings();
            }}
            label={<Text fz={12} ff="Inter" fw={500} color="#6B7280">{scoringEnabled ? "Enabled" : "Disabled"}</Text>}
            labelPosition="left"
            styles={{
              track: {
                backgroundColor: "#D1D5DB",
                "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" },
              },
              label: { paddingRight: 10 },
            }}
          />
        </Flex>
      </Box>
    );

    return (
      <Box>
        {/* Master toggle sits at the top on the Settings page, but in wizard
            mode it moves to the bottom just above Save/Cancel (see below). */}
        {!wizardHighlight && masterToggleCard}

        <Box className={classes.settingsCard} style={(!scoringEnabled && !wizardHighlight) ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
          <Text className={classes.settingsTitle}>Staff Compliance Score Weights</Text>
          <Text className={classes.settingsDescription} mt={4}>
            Configure how MAR, Tasks, and Notes contribute to staff compliance scores. Select a service type or use custom weights.
          </Text>
          <Divider my="sm" />

          {/* Service type selector */}
          <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={10} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>Service Type</Text>
          <Flex gap={8} mb={24} wrap="wrap">
            {presetOptions.map((opt) => {
              const isActive = activePreset === opt.key;
              return (
                <Box key={opt.key}
                  onClick={() => handlePresetSelect(opt.key)}
                  style={{
                    padding: "10px 20px", borderRadius: 10, cursor: "pointer",
                    backgroundColor: isActive ? "#FFAF4A" : "#F9FAFB",
                    border: isActive ? "1px solid #FFAF4A" : "1px solid #E5E7EB",
                    boxShadow: isActive ? "0 2px 8px rgba(255,175,74,0.25)" : "none",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "#FFF8EB"; e.currentTarget.style.borderColor = "#FFAF4A"; } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "#F9FAFB"; e.currentTarget.style.borderColor = "#E5E7EB"; } }}
                >
                  <Text ff="Inter" fz={13} fw={600} c={isActive ? "#171717" : "#6B7280"}>
                    {opt.label}
                  </Text>
                </Box>
              );
            })}
          </Flex>

          {/* Weight cards */}
          <Flex justify="space-between" align="center" mb={14}>
            <Text ff="Inter" fz={12} fw={600} c="#6B7280" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>Compliance Weights</Text>
            <Box style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20,
              backgroundColor: totalWeight === 100 ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${totalWeight === 100 ? "#A7F3D0" : "#FECACA"}`,
            }}>
              <Box style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: totalWeight === 100 ? "#10B981" : "#EF4444" }} />
              <Text ff="Inter" fz={11} fw={600} c={totalWeight === 100 ? "#059669" : "#DC2626"}>
                {totalWeight}%
              </Text>
            </Box>
          </Flex>
          <Grid gutter={14}>
            {[
              { key: "marWeight", label: "MAR", icon: <IconPill size={22} stroke={1.5} />, description: "Medication Administration", color: "#FFAF4A", bg: "#FFF8EB" },
              { key: "taskWeight", label: "Tasks", icon: <IconChecklist size={22} stroke={1.5} />, description: "Task Completion", color: "#FFAF4A", bg: "#FFF8EB" },
              { key: "notesWeight", label: "Notes", icon: <IconFileAnalytics size={22} stroke={1.5} />, description: "Clinical Notes", color: "#FFAF4A", bg: "#FFF8EB" },
            ].map((item) => {
              const val = settingsForm[item.key] || 0;
              return (
                <Grid.Col span={4} key={item.key}>
                  <Box style={{
                    padding: "20px 16px", borderRadius: 14, textAlign: "center",
                    backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB",
                    transition: "all 0.2s",
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#FFAF4A"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(255,175,74,0.1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <Box style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: item.bg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", color: item.color }}>
                      {item.icon}
                    </Box>
                    <Text ff="Inter" fz={14} fw={700} c="#171717" mb={2}>{item.label}</Text>
                    <Text ff="Inter" fz={11} c="#9CA3AF" mb={14}>{item.description}</Text>
                    <Box style={{ position: "relative", maxWidth: 100, margin: "0 auto" }}>
                      <NumberInput
                        min={0} max={100} radius={10} size="md"
                        value={val}
                        onChange={(v) => handleWeightEdit(item.key, v)}
                        hideControls
                        styles={{
                          input: { fontFamily: "Inter", fontSize: 22, fontWeight: 700, textAlign: "center", color: "#171717", backgroundColor: "#fff", border: "1px solid #E5E7EB", height: 48 },
                        }}
                      />
                      <Text ff="Inter" fz={14} fw={600} c="#9CA3AF" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>%</Text>
                    </Box>
                  </Box>
                </Grid.Col>
              );
            })}
          </Grid>
          {totalWeight !== 100 && (
            <Text ff="Inter" fz={12} c="#DC2626" mt={10} ta="center" fw={500}>Weights must total 100% to save. Currently at {totalWeight}%.</Text>
          )}
        </Box>

        {/* In wizard mode, the master Command Center Scoring toggle lives
            directly above Save/Cancel so admins flip it on right before
            closing the step. */}
        {wizardHighlight && <Box mt="md">{masterToggleCard}</Box>}

        {/* Save / Cancel */}
        <Box className={classes.settingsBtnGroup} mt={20}>
          <Button color="gray" size="md" radius="md" fullWidth onClick={handleResetSettings}>
            Cancel
          </Button>
          <Button color="yellow" size="md" radius="md" fullWidth loading={savingSettings} onClick={handleSaveSettings}>
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDocumentTemplatesSection = (category, folders) => {
    const { label, color } = CATEGORY_LABELS[category] || { label: category, color: "gray" };
    const safeFolders = Array.isArray(folders) ? folders : [];
    const allTemplates = safeFolders.flatMap((f) => f?.templates || []);
    const activeCount = allTemplates.filter((t) => t.active).length;

    return (
      <Box className={classes.sectionCard} key={category}>
        <Flex justify="space-between" align="center" mb={12}>
          <Flex align="center" gap={8}>
            <Text className={classes.sectionTitle}>{label}</Text>
            <Badge size="sm" color={color} variant="light" className={classes.sectionBadge}>
              {activeCount}/{allTemplates.length} active
            </Badge>
          </Flex>
          <Button
            variant="subtle"
            compact
            leftIcon={<IconFolderPlus size={15} />}
            color="gray"
            onClick={(e) => { e.stopPropagation(); setAddingFolderFor(addingFolderFor === category ? null : category); setNewFolderName(""); }}
            styles={{ root: { fontFamily: "Inter", fontSize: 12, fontWeight: 500, color: "#6B7280", "&:hover": { background: "#F3F4F6" } } }}
          >
            Add Folder
          </Button>
        </Flex>
        {/* Inline add folder input */}
        {addingFolderFor === category && (
          <Flex gap={8} mb={12} align="center">
            <TextInput
              placeholder="Enter folder name..."
              size="sm"
              radius="md"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              styles={{ root: { flex: 1 }, input: { fontFamily: "Inter", fontSize: 13 } }}
              autoFocus
            />
            <Button
              size="sm"
              radius="md"
              color="yellow"
              loading={addingFolderLoading}
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 13 } }}
            >
              Add
            </Button>
            <Button
              size="sm"
              radius="md"
              variant="subtle"
              color="gray"
              onClick={() => { setAddingFolderFor(null); setNewFolderName(""); }}
              styles={{ root: { fontFamily: "Inter", fontSize: 13 } }}
            >
              Cancel
            </Button>
          </Flex>
        )}
        <Divider mb={12} />
        {safeFolders.map((folderGroup, folderIndex) => {
          const folderKey = `${category}-${folderGroup.folder}`;
          const isExpanded = expandedFolders[folderKey];
          const folderTemplates = folderGroup?.templates || [];
          const folderActiveCount = folderTemplates.filter((t) => t.active).length;
          const isFolderActive = folderGroup.folderActive !== false;
          const isFirstStaffFolder = category === "staff" && folderIndex === 0;
          const addItemKey = `${category}|${folderGroup.folder}`;
          // Wizard onboarding highlight — guide the user to the first folder
          // while it's still collapsed, then shift the highlight onto the
          // template toggle switches once they expand it.
          const shouldHighlightFolder = wizardHighlight && folderIndex === 0 && !isExpanded;
          const shouldHighlightTemplates = wizardHighlight && folderIndex === 0 && isExpanded;

          return (
            <Box key={folderKey} mb={4} style={{ opacity: isFolderActive ? 1 : 0.5 }}>
              <Flex
                className={classes.folderHeader}
                align="center"
                justify="space-between"
                style={shouldHighlightFolder ? {
                  border: "2px solid #FFAF4A",
                  boxShadow: "0 0 0 4px rgba(255,175,74,0.18)",
                  background: "#FFFBF0",
                  transition: "all 0.2s ease",
                } : undefined}
                {...(isFirstStaffFolder ? { "data-tour": "template-folder" } : {})}
              >
                <Flex align="center" gap={8} onClick={() => toggleFolder(folderKey)} style={{ cursor: "pointer", flex: 1 }}>
                  {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                  <Text size="sm" weight={500} style={{ fontFamily: "Inter" }}>
                    {folderGroup.folder}
                  </Text>
                  <Badge size="xs" color={folderActiveCount === folderTemplates.length ? "green" : "yellow"} variant="light">
                    {folderActiveCount}/{folderTemplates.length}
                  </Badge>
                </Flex>
                <Flex align="center" gap={8}>
                  <Button
                    variant="subtle"
                    compact
                    size="xs"
                    leftIcon={<IconPlus size={13} />}
                    color="gray"
                    onClick={(e) => { e.stopPropagation(); setAddingItemFor(addingItemFor === addItemKey ? null : addItemKey); setNewItemName(""); if (!isExpanded) toggleFolder(folderKey); }}
                    styles={{ root: { fontFamily: "Inter", fontSize: 11, fontWeight: 500, color: "#9CA3AF", padding: "2px 8px", "&:hover": { background: "#F3F4F6", color: "#6B7280" } } }}
                  >
                    Add
                  </Button>
                  {folderGroup.folderSource === "custom" && (
                    <ActionIcon size="xs" color="red" variant="subtle" onClick={(e) => { e.stopPropagation(); handleDeleteFolder(category, folderGroup.folder); }} title="Delete folder">
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                  <Switch
                    checked={isFolderActive}
                    color="yellow"
                    size="xs"
                    disabled={togglingFolder === folderKey}
                    onChange={(e) => { e.stopPropagation(); handleToggleFolder(category, folderGroup.folder, isFolderActive); }}
                    styles={{ track: { cursor: "pointer" } }}
                  />
                </Flex>
              </Flex>
              {isExpanded && (
                <Box ml={8} mb={8} style={{ borderLeft: "2px solid #E5E5E5", borderRadius: "0 0 0 8px" }}
                  {...(isFirstStaffFolder ? { "data-tour": "template-items" } : {})}
                >
                  {/* Inline add item input */}
                  {addingItemFor === addItemKey && (
                    <Flex gap={8} p="8px 12px" align="center">
                      <TextInput
                        placeholder="Enter template name..."
                        size="xs"
                        radius="md"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.currentTarget.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateItem()}
                        styles={{ root: { flex: 1 }, input: { fontFamily: "Inter", fontSize: 12 } }}
                        autoFocus
                      />
                      <Button
                        size="xs"
                        radius="md"
                        color="yellow"
                        compact
                        loading={addingItemLoading}
                        onClick={handleCreateItem}
                        disabled={!newItemName.trim()}
                        styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 12 } }}
                      >
                        Add
                      </Button>
                      <Button
                        size="xs"
                        radius="md"
                        variant="subtle"
                        color="gray"
                        compact
                        onClick={() => { setAddingItemFor(null); setNewItemName(""); }}
                        styles={{ root: { fontFamily: "Inter", fontSize: 12 } }}
                      >
                        Cancel
                      </Button>
                    </Flex>
                  )}
                  {folderTemplates.map((template, templateIndex) => {
                    const toggleKey = `${category}-${folderGroup.folder}-${template.name}`;
                    return (
                      <Box className={classes.templateRow} key={toggleKey}
                        {...(isFirstStaffFolder && templateIndex === 0 ? { "data-tour": "template-toggle" } : {})}
                      >
                        <Flex align="center" gap={6}>
                          <Text className={classes.templateName}>{template.name}</Text>
                          {template.source === "custom" && (
                            <Badge size="xs" color="gray" variant="outline" styles={{ root: { fontFamily: "Inter", textTransform: "none", fontSize: 10 } }}>custom</Badge>
                          )}
                        </Flex>
                        <Flex align="center" gap={6}>
                          {template.source === "custom" && (
                            <ActionIcon size="xs" color="red" variant="subtle" onClick={() => handleDeleteItem(category, folderGroup.folder, template.name)} title="Delete template">
                              <IconTrash size={13} />
                            </ActionIcon>
                          )}
                          <Box
                            style={shouldHighlightTemplates ? {
                              padding: 4,
                              borderRadius: 999,
                              border: "2px solid #FFAF4A",
                              boxShadow: "0 0 0 4px rgba(255,175,74,0.18)",
                              background: "#FFFBF0",
                              transition: "all 0.2s ease",
                            } : undefined}
                          >
                            <Switch
                              checked={template.active}
                              color="yellow"
                              size="sm"
                              disabled={togglingTemplate === toggleKey || !isFolderActive}
                              onChange={() => handleToggleTemplate(category, folderGroup.folder, template.name, template.active)}
                              styles={{
                                track: { cursor: "pointer" },
                              }}
                            />
                          </Box>
                        </Flex>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // ── Add Compliance Item modal state ──
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [addItemType, setAddItemType] = useState("staff");
  const [addName, setAddName] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addCategory, setAddCategory] = useState("other");
  const [addFile, setAddFile] = useState(null);
  const [addUploading, setAddUploading] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addExpirationDate, setAddExpirationDate] = useState(null);
  const [addClientCategory, setAddClientCategory] = useState("document");
  const [addThreshold, setAddThreshold] = useState(95);
  const [addFrequencyMonths, setAddFrequencyMonths] = useState(12);
  const [addGracePeriodDays, setAddGracePeriodDays] = useState(30);
  const [addFolderSelect, setAddFolderSelect] = useState("");

  // Build folder options based on selected entity type
  const getAddFolderOptions = () => {
    if (!documentTemplates) return [];
    const folders = documentTemplates[addItemType];
    if (!Array.isArray(folders)) return [];
    return folders.map((f) => ({ value: f.folder, label: f.folder }));
  };

  const resetAddForm = () => {
    setAddName(""); setAddDescription(""); setAddCategory("other");
    setAddFile(null); setAddUploading(false); setAddExpirationDate(null);
    setAddClientCategory("document"); setAddThreshold(95);
    setAddFrequencyMonths(12); setAddGracePeriodDays(30);
    setAddFolderSelect("");
  };

  const handleAddSubmit = async () => {
    if (!addName.trim()) return swal("Error", "Name is required", "error");
    setAddSubmitting(true);
    let success;

    // If a folder is selected, also add the item as a custom document template
    if (addFolderSelect) {
      const folderSuccess = await addCustomItem(addItemType, addFolderSelect, addName.trim());
      if (!folderSuccess) {
        setAddSubmitting(false);
        return;
      }
    }

    // All document types now use the custom item/folder system
    // Staff also creates a credential type for backward compatibility
    if (addItemType === "staff") {
      success = await addCredentialType({ name: addName.trim(), description: addDescription.trim() });
    } else {
      // Facility and client templates are added purely through the folder system above
      success = true;
    }
    setAddSubmitting(false);
    if (success) { resetAddForm(); closeAddModal(); getDocumentTemplates(); }
  };

  const addFieldStyles = { label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4, fontSize: 13 } };

  const renderDocumentTemplates = () => {
    if (!documentTemplates || typeof documentTemplates !== "object") {
      return <Flex align="center" justify="center" py={60}><Loader color="#FFAF4A" /></Flex>;
    }

    return (
      <Box>
        <Box className={classes.settingsCard} mb={16} data-tour="document-templates">
          <Flex justify="space-between" align="center">
            <Box>
              <Flex align="center" gap={8}>
                <Text className={classes.settingsTitle}>Document Templates</Text>
                {documentTemplates?.source === "compliance-engine" && (
                  <Badge size="sm" variant="light" color="indigo" radius="sm" styles={{ root: { fontFamily: "Inter", textTransform: "none" } }}>
                    State-specific
                  </Badge>
                )}
              </Flex>
              <Text className={classes.settingsDescription} mt={4}>
                {documentTemplates?.source === "compliance-engine"
                  ? `Templates generated from compliance engine for: ${(documentTemplates?.states || []).join(", ")}. Inactive templates will not be created in staff, facility, or client document folders.`
                  : "Manage which document templates are required for compliance. Inactive templates will not be created in staff, facility, or client document folders."
                }
              </Text>
            </Box>
            {!wizardHighlight && (
              <Button
                radius="md"
                leftIcon={<IconPlus size={16} />}
                onClick={openAddModal}
                styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 13, background: "linear-gradient(135deg, #D97706, #F59E0B)", border: "none", whiteSpace: "nowrap", "&:hover": { background: "linear-gradient(135deg, #B45309, #D97706)" } } }}
              >
                Add Document Template
              </Button>
            )}
          </Flex>
          <Divider my="sm" />
          {(!embeddedDocCategory || embeddedDocCategory === "staff") && documentTemplates?.staff && renderDocumentTemplatesSection("staff", documentTemplates.staff)}
          {(!embeddedDocCategory || embeddedDocCategory === "facility") && documentTemplates?.facility && renderDocumentTemplatesSection("facility", documentTemplates.facility)}
          {(!embeddedDocCategory || embeddedDocCategory === "client") && documentTemplates?.client && renderDocumentTemplatesSection("client", documentTemplates.client)}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.wrapper}>
      <Tabs
        value={activeSubTab}
        onTabChange={setActiveSubTab}
        styles={{
          tab: {
            fontFamily: "Inter",
            fontSize: 14,
            fontWeight: 500,
            padding: "10px 20px",
            color: "#000",
            "&[data-active]": {
              borderBottomColor: "#171717",
              color: "#171717",
              fontWeight: 600,
            },
          },
          tabsList: {
            borderBottom: "1px solid #E5E5E5",
            marginBottom: 20,
          },
        }}
      >
        {!embeddedSubTab && (
          <Tabs.List>
            <Tabs.Tab value="documents" data-tour="settings-documents-tab">Document Templates</Tabs.Tab>
            <Tabs.Tab value="charting" data-tour="settings-charting-tab">Charting</Tabs.Tab>
            <Tabs.Tab value="settings">Settings</Tabs.Tab>
          </Tabs.List>
        )}

        <Tabs.Panel value="documents">
          {renderDocumentTemplates()}
        </Tabs.Panel>

        <Tabs.Panel value="charting">
          <Box>
            <Flex justify="space-between" align="center" mb={16}>
              <Box>
                <Text className={classes.settingsTitle}>Charting Compliance Rules</Text>
                <Text fz={13} color="dimmed" ff="Inter" mt={4}>
                  Select or Add new charting rules to count towards your compliance score.
                </Text>
              </Box>
              <Button
                radius="md"
                leftIcon={<IconPlus size={16} />}
                onClick={() => openChartRuleModal()}
                styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 13, background: "#FFAF4A", color: "#171717", border: "none", whiteSpace: "nowrap", "&:hover": { background: "#F5A623" } } }}
              >
                Add Charting Rule
              </Button>
            </Flex>

            {loading ? (
              <Flex align="center" justify="center" py={60}><Loader color="#FFAF4A" /></Flex>
            ) : (
              <SimpleGrid cols={3} breakpoints={[{ maxWidth: "md", cols: 2 }, { maxWidth: "sm", cols: 1 }]}>
                {/* ── Note Card ── */}
                {(() => {
                  const noteRules = chartingRules.filter(r => r.category === "notes");
                  const notesActive = settings?.defaultNotesActive !== false;
                  const noteRuleActive = settings?.defaultNoteRuleActive !== false;
                  return (
                    <Paper p={20} radius={16} style={{
                      border: wizardHighlight ? "2px solid #FFAF4A" : "1px solid #E5E7EB",
                      boxShadow: wizardHighlight ? "0 0 0 4px rgba(255,175,74,0.18)" : undefined,
                      background: wizardHighlight ? "#FFFBF0" : undefined,
                      opacity: notesActive ? 1 : 0.5,
                      transition: "all 0.2s ease",
                    }}>
                      <Group position="apart" mb={8}>
                        <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>NOTE</Badge>
                        <Box style={wizardHighlight ? {
                          padding: 4,
                          borderRadius: 999,
                          border: "2px solid #FFAF4A",
                          boxShadow: "0 0 0 4px rgba(255,175,74,0.18)",
                          background: "#fff",
                          transition: "all 0.2s ease",
                        } : undefined}>
                          <Switch size="sm" checked={notesActive}
                            onChange={async () => { await updateComplianceSettings({ defaultNotesActive: !notesActive }); getComplianceSettings(); }}
                            label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{notesActive ? "Active" : "Inactive"}</Text>}
                            labelPosition="left"
                            styles={{ track: { backgroundColor: "#D1D5DB", "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" } }, label: { paddingRight: 6 } }}
                          />
                        </Box>
                      </Group>
                      <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>Note</Text>
                      <Text fz={12} ff="Inter" color="#9CA3AF" mb={12}>Default: Progress Note{noteRules.length > 0 ? ` + ${noteRules.length} rule${noteRules.length > 1 ? "s" : ""}` : ""}</Text>
                      <Divider mb={12} />
                      {/* Default rule */}
                      <Flex align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: noteRuleActive ? "#F9FAFB" : "#F3F4F6", borderRadius: 8, opacity: noteRuleActive ? 1 : 0.5 }}>
                        <Text fz={13} fw={500} ff="Inter" color="#374151" style={{ flex: 1 }}>Progress Note</Text>
                        <Badge variant="light" color="gray" size="xs" mr={4} style={{ cursor: "pointer" }} onClick={() => noteRuleActive && openScheduleModal("note")}>
                          {settings?.defaultNoteSchedule?.frequency === "weekly" ? `Weekly` : settings?.defaultNoteSchedule?.frequency === "monthly" ? `Monthly` : "Daily"}
                        </Badge>
                        <Button size="xs" compact variant="subtle" color="dark" ff="Inter" fz={10} fw={600}
                          styles={{ root: { padding: "2px 8px", "&:hover": { backgroundColor: "#F3F4F6" } } }}
                          onClick={() => openScheduleModal("note")} disabled={!noteRuleActive}>
                          Set Schedule
                        </Button>
                        <Badge variant="light" color="gray" size="xs" mr={4}>Default</Badge>
                        <Switch size="xs" checked={noteRuleActive}
                          onChange={async () => { await updateComplianceSettings({ defaultNoteRuleActive: !noteRuleActive }); getComplianceSettings(); }}
                          styles={{ track: { backgroundColor: "#D1D5DB", "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" } } }}
                        />
                      </Flex>
                      {/* Additional rules */}
                      {noteRules.length > 0 && (
                        <Box mt={12} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
                          <Text fz={12} fw={600} ff="Inter" color="#6B7280" mb={8}>Additional Rules ({noteRules.length})</Text>
                          {noteRules.map(rule => (
                            <Flex key={rule._id || rule.id} align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                              <Box style={{ flex: 1 }}>
                                <Text fz={13} fw={500} ff="Inter" color="#374151">{rule.name}</Text>
                                {rule.noteType?.name && <Text fz={11} ff="Inter" color="#9CA3AF">Note Type: {rule.noteType.name}</Text>}
                              </Box>
                              <Flex gap={4}>
                                <ActionIcon size="sm" variant="subtle" color="dark" onClick={() => openChartRuleModal(rule)}><IconEdit size={14} /></ActionIcon>
                                <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteChartRule(rule._id || rule.id)}><IconTrash size={14} /></ActionIcon>
                              </Flex>
                            </Flex>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  );
                })()}

                {/* ── Task Card ── */}
                {(() => {
                  const taskRules = chartingRules.filter(r => r.category === "task");
                  const tasksActive = settings?.defaultTaskActive !== false;
                  const taskRuleActive = settings?.defaultTaskRuleActive !== false;
                  return (
                    <Paper p={20} radius={16} style={{ border: "1px solid #E5E7EB", opacity: tasksActive ? 1 : 0.5 }}>
                      <Group position="apart" mb={8}>
                        <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>TASK</Badge>
                        <Switch size="sm" checked={tasksActive}
                          onChange={async () => { await updateComplianceSettings({ defaultTaskActive: !tasksActive }); getComplianceSettings(); }}
                          label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{tasksActive ? "Active" : "Inactive"}</Text>}
                          labelPosition="left"
                          styles={{ track: { backgroundColor: "#D1D5DB", "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" } }, label: { paddingRight: 6 } }}
                        />
                      </Group>
                      <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>Task</Text>
                      <Text fz={12} ff="Inter" color="#9CA3AF" mb={12}>Default: Activities of Daily Living{taskRules.length > 0 ? ` + ${taskRules.length} rule${taskRules.length > 1 ? "s" : ""}` : ""}</Text>
                      <Divider mb={12} />
                      {/* Default rule */}
                      <Flex align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: taskRuleActive ? "#F9FAFB" : "#F3F4F6", borderRadius: 8, opacity: taskRuleActive ? 1 : 0.5 }}>
                        <Text fz={13} fw={500} ff="Inter" color="#374151" style={{ flex: 1 }}>Activities of Daily Living</Text>
                        <Badge variant="light" color="gray" size="xs" mr={4} style={{ cursor: "pointer" }} onClick={() => taskRuleActive && openScheduleModal("task")}>
                          {settings?.defaultTaskSchedule?.frequency === "weekly" ? `Weekly` : settings?.defaultTaskSchedule?.frequency === "monthly" ? `Monthly` : "Daily"}
                        </Badge>
                        <Button size="xs" compact variant="subtle" color="dark" ff="Inter" fz={10} fw={600}
                          styles={{ root: { padding: "2px 8px", "&:hover": { backgroundColor: "#F3F4F6" } } }}
                          onClick={() => openScheduleModal("task")} disabled={!taskRuleActive}>
                          Set Schedule
                        </Button>
                        <Badge variant="light" color="gray" size="xs" mr={4}>Default</Badge>
                        <Switch size="xs" checked={taskRuleActive}
                          onChange={async () => { await updateComplianceSettings({ defaultTaskRuleActive: !taskRuleActive }); getComplianceSettings(); }}
                          styles={{ track: { backgroundColor: "#D1D5DB", "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" } } }}
                        />
                      </Flex>
                      {/* Additional rules */}
                      {taskRules.length > 0 && (
                        <Box mt={12} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
                          <Text fz={12} fw={600} ff="Inter" color="#6B7280" mb={8}>Additional Rules ({taskRules.length})</Text>
                          {taskRules.map(rule => (
                            <Flex key={rule._id || rule.id} align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                              <Box style={{ flex: 1 }}>
                                <Text fz={13} fw={500} ff="Inter" color="#374151">{rule.name}</Text>
                                {rule.taskType?.name && <Text fz={11} ff="Inter" color="#9CA3AF">Task Type: {rule.taskType.name}</Text>}
                              </Box>
                              <Flex gap={4}>
                                <ActionIcon size="sm" variant="subtle" color="dark" onClick={() => openChartRuleModal(rule)}><IconEdit size={14} /></ActionIcon>
                                <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteChartRule(rule._id || rule.id)}><IconTrash size={14} /></ActionIcon>
                              </Flex>
                            </Flex>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  );
                })()}

                {/* ── MAR Card ── */}
                {(() => {
                  const marRules = chartingRules.filter(r => r.category === "mar");
                  const marActive = settings?.defaultMarActive !== false;
                  return (
                    <Paper p={20} radius={16} style={{ border: "1px solid #E5E7EB", opacity: marActive ? 1 : 0.5 }}>
                      <Group position="apart" mb={8}>
                        <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>MAR</Badge>
                        <Switch size="sm" checked={marActive}
                          onChange={async () => { await updateComplianceSettings({ defaultMarActive: !marActive }); getComplianceSettings(); }}
                          label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{marActive ? "Active" : "Inactive"}</Text>}
                          labelPosition="left"
                          styles={{ track: { backgroundColor: "#D1D5DB", "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" } }, label: { paddingRight: 6 } }}
                        />
                      </Group>
                      <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>MAR</Text>
                      <Text fz={12} ff="Inter" color="#9CA3AF" mb={12}>Default: Missed MARs{marRules.length > 0 ? ` + ${marRules.length} rule${marRules.length > 1 ? "s" : ""}` : ""}</Text>
                      <Divider mb={12} />
                      {/* Default rule */}
                      <Flex align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                        <Text fz={13} fw={500} ff="Inter" color="#374151" style={{ flex: 1 }}>Missed MARs</Text>
                        <Badge variant="light" color="gray" size="xs" mr={4} style={{ cursor: "pointer" }} onClick={() => openScheduleModal("mar")}>
                          {settings?.defaultMarSchedule?.frequency === "weekly" ? `Weekly` : settings?.defaultMarSchedule?.frequency === "monthly" ? `Monthly` : "Daily"}
                        </Badge>
                        <Button size="xs" compact variant="subtle" color="dark" ff="Inter" fz={10} fw={600}
                          styles={{ root: { padding: "2px 8px", "&:hover": { backgroundColor: "#F3F4F6" } } }}
                          onClick={() => openScheduleModal("mar")}>
                          Set Schedule
                        </Button>
                        <Badge variant="light" color="gray" size="xs">Default</Badge>
                      </Flex>
                      {/* Additional rules */}
                      {marRules.length > 0 && (
                        <Box mt={12} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
                          <Text fz={12} fw={600} ff="Inter" color="#6B7280" mb={8}>Additional Rules ({marRules.length})</Text>
                          {marRules.map(rule => (
                            <Flex key={rule._id || rule.id} align="center" gap={8} mb={6} style={{ padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                              <Box style={{ flex: 1 }}>
                                <Text fz={13} fw={500} ff="Inter" color="#374151">{rule.name}</Text>
                              </Box>
                              <Flex gap={4}>
                                <ActionIcon size="sm" variant="subtle" color="dark" onClick={() => openChartRuleModal(rule)}><IconEdit size={14} /></ActionIcon>
                                <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteChartRule(rule._id || rule.id)}><IconTrash size={14} /></ActionIcon>
                              </Flex>
                            </Flex>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  );
                })()}
              </SimpleGrid>
            )}
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value="settings">
          {renderSettings()}
        </Tabs.Panel>
      </Tabs>

      {/* Add Compliance Item Modal */}
      <Modal
        opened={addModalOpened}
        onClose={() => { resetAddForm(); closeAddModal(); }}
        centered
        size={540}
        radius={16}
        padding={0}
        withCloseButton={false}
        styles={{ body: { padding: 0 }, content: { overflow: "hidden", borderRadius: 16 } }}
         overlayProps={{ opacity: 0.5, blur: 0.5 }}
        zIndex={2700}
        overlayOpacity={0.5}
      >
        {/* ── Gradient header ── */}
        <Box style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)", padding: "22px 28px 18px", position: "relative" }}>
          <Box
            onClick={() => { resetAddForm(); closeAddModal(); }}
            style={{
              position: "absolute", top: 14, right: 14,
              width: 30, height: 30, borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.35)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)")}
          >
            <Text fz={16} c="#fff" lh={1} fw={500}>&#x2715;</Text>
          </Box>
          <Flex align="center" gap={12}>
            <Box style={{
              width: 40, height: 40, borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IconShieldCheck size={22} color="#fff" stroke={2} />
            </Box>
            <Box>
              <Text ff="Inter" fz={20} fw={800} c="#fff" lh={1.2}>Add Document Template</Text>
              <Text ff="Inter" fz={12} c="rgba(255,255,255,0.7)" mt={2}>Add a new document template for staff, facility, or clients</Text>
            </Box>
          </Flex>
        </Box>

        {/* ── Body ── */}
        <Box style={{ padding: "20px 28px 24px", maxHeight: "calc(100vh - 280px)", overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: "#D1D5DB transparent" }}>

          {/* Section: Compliance Type */}
          <Box mb={20}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Document Type
              </Text>
            </Flex>
            <Flex gap={10}>
              {[
                { value: "staff", label: "Staff Document" },
                { value: "facility", label: "Facility Document" },
                { value: "client", label: "Client Document" },
              ].map((t) => (
                <Box
                  key={t.value}
                  onClick={() => { setAddItemType(t.value); setAddFolderSelect(""); }}
                  style={{
                    flex: 1, padding: "14px 12px", borderRadius: 12, cursor: "pointer", textAlign: "center",
                    border: `2px solid ${addItemType === t.value ? "#F59E0B" : "#E5E7EB"}`,
                    backgroundColor: addItemType === t.value ? "#FFFBEB" : "#FAFAFA",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Text ff="Inter" fz={13} fw={700} c={addItemType === t.value ? "#92400E" : "#374151"}>{t.label}</Text>
                </Box>
              ))}
            </Flex>
          </Box>

          {/* Section: Folder Selection */}
          <Box mb={20}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Folder
              </Text>
            </Flex>
            <Select
              placeholder="Select a folder to add this item to..."
              data={getAddFolderOptions()}
              value={addFolderSelect}
              onChange={(val) => setAddFolderSelect(val)}
              searchable
              creatable
              getCreateLabel={(query) => `+ Create folder "${query}"`}
              onCreate={(query) => {
                setAddFolderSelect(query);
                return { value: query, label: query };
              }}
              styles={{
                input: {
                  fontFamily: "Inter", fontSize: 13, borderRadius: 10,
                  border: "1.5px solid #E5E7EB", padding: "10px 14px", height: "auto",
                  "&:focus": { borderColor: "#F59E0B" },
                  "&::placeholder": { color: "#9CA3AF" },
                },
                item: { fontFamily: "Inter", fontSize: 13 },
              }}
            />
            <Text ff="Inter" fz={11} c="#9CA3AF" mt={4}>Select an existing folder or type to create a new one</Text>
          </Box>

          {/* Section: Item Details */}
          <Box mb={20}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Item Details
              </Text>
            </Flex>

            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Name <span style={{ color: "#EF4444" }}>*</span></Text>
            <TextInput
              placeholder="Enter compliance item name"
              required
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              mb={14}
              styles={{
                label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                input: {
                  fontFamily: "Inter", fontSize: 15, fontWeight: 600, borderRadius: 10,
                  border: "1.5px solid #E5E7EB", padding: "12px 14px", height: "auto",
                  "&:focus": { borderColor: "#F59E0B" },
                  "&::placeholder": { fontWeight: 400, color: "#9CA3AF" },
                },
              }}
            />
            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Description</Text>
            <Textarea
              placeholder="Provide details about this compliance requirement..."
              value={addDescription}
              onChange={(e) => setAddDescription(e.target.value)}
              mb={0}
              minRows={3}
              autosize
              maxRows={6}
              styles={{
                label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                input: {
                  fontFamily: "Inter", fontSize: 13, lineHeight: 1.6, borderRadius: 10,
                  border: "1.5px solid #E5E7EB",
                  "&:focus": { borderColor: "#F59E0B" },
                  "&::placeholder": { color: "#BCBCBC" },
                },
              }}
            />
          </Box>

          {/* Configuration section removed — all document types now use name + folder only */}
        </Box>

        {/* ── Footer ── */}
        <Box style={{ padding: "16px 28px 20px", borderTop: "1px solid #F3F4F6", backgroundColor: "#FAFAFA" }}>
          <Flex justify="space-between" align="center">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => { resetAddForm(); closeAddModal(); }}
              radius={10}
              styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 14, color: "#6B7280", padding: "8px 20px" } }}
            >
              Cancel
            </Button>
            <Button
              loading={addSubmitting || addUploading}
              onClick={handleAddSubmit}
              radius={10}
              styles={{
                root: {
                  fontFamily: "Inter", fontWeight: 700, fontSize: 14,
                  background: "linear-gradient(135deg, #D97706, #F59E0B)",
                  border: "none", padding: "10px 32px", height: "auto",
                  "&:hover": { background: "linear-gradient(135deg, #B45309, #D97706)" },
                },
              }}
            >
              Add Item
            </Button>
          </Flex>
        </Box>
      </Modal>

      {/* Charting Rule Modal — redesigned to match ComplianceTaskModal */}
      <Modal
        opened={chartRuleModalOpen}
        onClose={() => { resetChartRuleForm(); setChartRuleModalOpen(false); }}
        centered
        size={540}
        radius={16}
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
          content: { overflow: "hidden", borderRadius: 16 },
        }}
         overlayProps={{ opacity: 0.5, blur: 0.5 }}
        zIndex={2700}
        overlayOpacity={0.5}
      >
        {/* ── Gradient header ── */}
        <Box style={{
          background: "linear-gradient(135deg, #D97706, #F59E0B)",
          padding: "22px 28px 18px",
          position: "relative",
        }}>
          {/* Close button */}
          <Box
            onClick={() => { resetChartRuleForm(); setChartRuleModalOpen(false); }}
            style={{
              position: "absolute", top: 14, right: 14,
              width: 30, height: 30, borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.35)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)")}
          >
            <Text fz={16} c="#fff" lh={1} fw={500}>&#x2715;</Text>
          </Box>

          {/* Icon + Title */}
          <Flex align="center" gap={12}>
            <Box style={{
              width: 40, height: 40, borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IconClipboardCheck size={22} color="#fff" stroke={2} />
            </Box>
            <Box>
              <Text ff="Inter" fz={20} fw={800} c="#fff" lh={1.2}>
                {editingChartRule ? "Edit Charting Rule" : "Add Charting Rule"}
              </Text>
              <Text ff="Inter" fz={12} c="rgba(255,255,255,0.7)" mt={2}>
                {editingChartRule ? "Update the compliance requirement details" : "Create a new compliance tracking requirement"}
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* ── Body ── */}
        <Box style={{
          padding: "20px 28px 24px", maxHeight: "calc(100vh - 280px)", overflowY: "auto",
          scrollbarWidth: "thin", scrollbarColor: "#D1D5DB transparent",
        }}>

          {/* Section: Rule Details */}
          <Box mb={20}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Rule Details
              </Text>
            </Flex>

            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Requirement Name <span style={{ color: "#EF4444" }}>*</span></Text>
            <TextInput
              placeholder="e.g. Progress Notes, TB Test, Daily Tasks"
              required
              value={chartRuleName}
              onChange={(e) => setChartRuleName(e.currentTarget.value)}
              mb={14}
              styles={{
                label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                input: {
                  fontFamily: "Inter", fontSize: 15, fontWeight: 600, borderRadius: 10,
                  border: "1.5px solid #E5E7EB", padding: "12px 14px", height: "auto",
                  "&:focus": { borderColor: "#F59E0B" },
                  "&::placeholder": { fontWeight: 400, color: "#9CA3AF" },
                },
              }}
            />

            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Description</Text>
            <Textarea
              placeholder="Describe what this compliance rule tracks — context, expectations, or criteria..."
              value={chartRuleDescription}
              onChange={(e) => setChartRuleDescription(e.currentTarget.value)}
              mb={0}
              minRows={3}
              autosize
              maxRows={6}
              styles={{
                label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                input: {
                  fontFamily: "Inter", fontSize: 13, lineHeight: 1.6, borderRadius: 10,
                  border: "1.5px solid #E5E7EB",
                  "&:focus": { borderColor: "#F59E0B" },
                  "&::placeholder": { color: "#BCBCBC" },
                },
              }}
            />
          </Box>

          {/* Section: Configuration */}
          <Box mb={20}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Configuration
              </Text>
            </Flex>

            {/* Category toggle cards */}
            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={8}>Category <span style={{ color: "#EF4444" }}>*</span></Text>
            <Flex gap={10} mb={16}>
              {[
                { value: "notes", label: "Notes", desc: "Track note completion" },
                { value: "task", label: "Task", desc: "Track task completion" },
              ].map((cat) => (
                <Box
                  key={cat.value}
                  onClick={() => {
                    setChartRuleCategory(cat.value);
                    if (cat.value !== "notes") setChartRuleNoteType(null);
                    if (cat.value !== "task") setChartRuleTaskType(null);
                  }}
                  style={{
                    flex: 1, padding: "12px 14px", borderRadius: 12, cursor: "pointer",
                    border: `2px solid ${chartRuleCategory === cat.value ? "#F59E0B" : "#E5E7EB"}`,
                    backgroundColor: chartRuleCategory === cat.value ? "#FFFBEB" : "#FAFAFA",
                    transition: "all 0.2s ease", textAlign: "center",
                  }}
                >
                  <Text ff="Inter" fz={14} fw={700} c={chartRuleCategory === cat.value ? "#92400E" : "#374151"}>
                    {cat.label}
                  </Text>
                  <Text ff="Inter" fz={10} c={chartRuleCategory === cat.value ? "#D97706" : "#9CA3AF"} fw={500}>
                    {cat.desc}
                  </Text>
                </Box>
              ))}
            </Flex>

            {/* Note Type — conditional */}
            {chartRuleCategory === "notes" && (
              <>
                <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Note Type <span style={{ color: "#EF4444" }}>*</span></Text>
                <Select
                  placeholder="Select note type to track"
                  searchable
                  required
                  data={(noteTypes || []).map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
                  value={chartRuleNoteType}
                  onChange={setChartRuleNoteType}
                  mb={14}
                  styles={{
                    label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                    input: {
                      fontFamily: "Inter", fontSize: 14, borderRadius: 10,
                      border: "1.5px solid #E5E7EB",
                      "&:focus": { borderColor: "#F59E0B" },
                    },
                  }}
                />
              </>
            )}

            {/* Task Type — conditional */}
            {chartRuleCategory === "task" && (
              <>
                <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Task Type <span style={{ color: "#EF4444" }}>*</span></Text>
                <Select
                  placeholder="Select task type to track"
                  searchable
                  required
                  data={(taskTypes || []).map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
                  value={chartRuleTaskType}
                  onChange={setChartRuleTaskType}
                  mb={14}
                  styles={{
                    label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                    input: {
                      fontFamily: "Inter", fontSize: 14, borderRadius: 10,
                      border: "1.5px solid #E5E7EB",
                      "&:focus": { borderColor: "#F59E0B" },
                    },
                  }}
                />
              </>
            )}

            {/* Completion Threshold */}
            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Completion Threshold</Text>
            <Box style={{
              backgroundColor: "#F9FAFB", borderRadius: 12, padding: 16,
              border: "1px solid #F3F4F6",
            }}>
              <NumberInput
                placeholder="95"
                min={0}
                max={100}
                value={chartRuleThreshold}
                onChange={setChartRuleThreshold}
                mb={6}
                styles={{
                  label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
                  input: {
                    fontFamily: "Inter", fontSize: 14, borderRadius: 10,
                    border: "1.5px solid #E5E7EB",
                    "&:focus": { borderColor: "#F59E0B" },
                  },
                }}
              />
              <Text ff="Inter" fz={11} c="#D97706" fw={500}>
                Clients below {chartRuleThreshold}% completion will be flagged as non-compliant
              </Text>
            </Box>

            {/* Schedule */}
            <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6} mt={16}>Schedule</Text>
            <Box style={{ backgroundColor: "#F9FAFB", borderRadius: 12, padding: 16, border: "1px solid #F3F4F6" }}>
              <Text ff="Inter" fz={11} c="#6B7280" mb={8}>Which days should this rule count for compliance?</Text>
              <Flex gap={8} mb={12}>
                {[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ].map((opt) => (
                  <Box key={opt.value}
                    onClick={() => { setChartRuleFrequency(opt.value); if (opt.value === "daily") { setChartRuleDaysOfWeek([]); setChartRuleDaysOfMonth([]); } }}
                    style={{
                      padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                      backgroundColor: chartRuleFrequency === opt.value ? "#FFAF4A" : "#fff",
                      border: chartRuleFrequency === opt.value ? "1px solid #FFAF4A" : "1px solid #E5E7EB",
                      transition: "all 0.15s",
                    }}>
                    <Text ff="Inter" fz={12} fw={chartRuleFrequency === opt.value ? 600 : 500}
                      c={chartRuleFrequency === opt.value ? "#171717" : "#6B7280"}>
                      {opt.label}
                    </Text>
                  </Box>
                ))}
              </Flex>

              {chartRuleFrequency === "weekly" && (
                <Flex gap={6}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => {
                    const isSelected = chartRuleDaysOfWeek.includes(idx);
                    return (
                      <Box key={idx}
                        onClick={() => setChartRuleDaysOfWeek((prev) => isSelected ? prev.filter((d) => d !== idx) : [...prev, idx])}
                        style={{
                          width: 40, height: 40, borderRadius: 8, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          backgroundColor: isSelected ? "#171717" : "#fff",
                          border: isSelected ? "1px solid #171717" : "1px solid #E5E7EB",
                          transition: "all 0.15s",
                        }}>
                        <Text ff="Inter" fz={11} fw={600} c={isSelected ? "#fff" : "#6B7280"}>{day}</Text>
                      </Box>
                    );
                  })}
                </Flex>
              )}

              {chartRuleFrequency === "monthly" && (
                <Box>
                  <Text ff="Inter" fz={11} c="#6B7280" mb={8}>Select days of the month</Text>
                  <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                      const isSelected = chartRuleDaysOfMonth.includes(day);
                      return (
                        <Box key={day}
                          onClick={() => setChartRuleDaysOfMonth((prev) => isSelected ? prev.filter((d) => d !== day) : [...prev, day])}
                          style={{
                            height: 36, borderRadius: 8, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            backgroundColor: isSelected ? "#171717" : "#fff",
                            border: isSelected ? "1px solid #171717" : "1px solid #E5E7EB",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "#fff"; }}>
                          <Text ff="Inter" fz={12} fw={600} c={isSelected ? "#fff" : "#374151"}>{day}</Text>
                        </Box>
                      );
                    })}
                  </Box>
                  <Text ff="Inter" fz={10} c="#9CA3AF" mt={6}>{chartRuleDaysOfMonth.length} day{chartRuleDaysOfMonth.length !== 1 ? "s" : ""} selected</Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box style={{
          padding: "16px 28px 20px",
          borderTop: "1px solid #F3F4F6",
          backgroundColor: "#FAFAFA",
        }}>
          <Flex justify="space-between" align="center">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => { resetChartRuleForm(); setChartRuleModalOpen(false); }}
              radius={10}
              styles={{
                root: { fontFamily: "Inter", fontWeight: 600, fontSize: 14, color: "#6B7280", padding: "8px 20px" },
              }}
            >
              Cancel
            </Button>
            <Button
              loading={chartRuleSubmitting || addLoading}
              onClick={handleChartRuleSubmit}
              disabled={!chartRuleName.trim() || !chartRuleCategory}
              radius={10}
              styles={{
                root: {
                  fontFamily: "Inter", fontWeight: 700, fontSize: 14,
                  background: "linear-gradient(135deg, #D97706, #F59E0B)",
                  border: "none", padding: "10px 32px", height: "auto",
                  "&:hover": { background: "linear-gradient(135deg, #B45309, #D97706)" },
                },
              }}
            >
              {editingChartRule ? "Update Rule" : "Add Rule"}
            </Button>
          </Flex>
        </Box>
      </Modal>
      {/* ── Schedule Modal for Default Rules ── */}
      <Modal
        opened={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        title={<Text ff="Inter" fw={700} fz={18}>Set Schedule — {scheduleModalType === "note" ? "Progress Note" : scheduleModalType === "task" ? "Activities of Daily Living" : "Missed MARs"}</Text>}
        centered
        radius={14}
        size={420}
        zIndex={2700}
      >
        <Text ff="Inter" fz={13} c="#6B7280" mb={16}>Choose which days this rule counts for compliance scoring.</Text>

        {/* Frequency pills */}
        <Text ff="Inter" fz={12} fw={600} c="#374151" mb={8}>Frequency</Text>
        <Flex gap={8} mb={20}>
          {[
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "monthly", label: "Monthly" },
          ].map((opt) => (
            <Box key={opt.value}
              onClick={() => { setScheduleModalFreq(opt.value); if (opt.value === "daily") { setScheduleModalDaysOfWeek([]); setScheduleModalDaysOfMonth([]); } }}
              style={{
                padding: "8px 18px", borderRadius: 8, cursor: "pointer",
                backgroundColor: scheduleModalFreq === opt.value ? "#FFAF4A" : "#F9FAFB",
                border: scheduleModalFreq === opt.value ? "1px solid #FFAF4A" : "1px solid #E5E7EB",
                transition: "all 0.15s",
              }}>
              <Text ff="Inter" fz={13} fw={600} c={scheduleModalFreq === opt.value ? "#171717" : "#6B7280"}>{opt.label}</Text>
            </Box>
          ))}
        </Flex>

        {/* Weekly: day toggles */}
        {scheduleModalFreq === "weekly" && (
          <Box mb={16}>
            <Text ff="Inter" fz={12} fw={600} c="#374151" mb={8}>Select Days</Text>
            <Flex gap={6}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => {
                const sel = scheduleModalDaysOfWeek.includes(idx);
                return (
                  <Box key={idx}
                    onClick={() => setScheduleModalDaysOfWeek((prev) => sel ? prev.filter((d) => d !== idx) : [...prev, idx])}
                    style={{
                      flex: 1, height: 40, borderRadius: 8, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundColor: sel ? "#171717" : "#fff",
                      border: sel ? "1px solid #171717" : "1px solid #E5E7EB",
                      transition: "all 0.15s",
                    }}>
                    <Text ff="Inter" fz={11} fw={600} c={sel ? "#fff" : "#6B7280"}>{day}</Text>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        )}

        {/* Monthly: calendar grid */}
        {scheduleModalFreq === "monthly" && (
          <Box mb={16}>
            <Text ff="Inter" fz={12} fw={600} c="#374151" mb={8}>Select Days of Month</Text>
            <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                const sel = scheduleModalDaysOfMonth.includes(day);
                return (
                  <Box key={day}
                    onClick={() => setScheduleModalDaysOfMonth((prev) => sel ? prev.filter((d) => d !== day) : [...prev, day])}
                    style={{
                      height: 38, borderRadius: 8, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundColor: sel ? "#171717" : "#fff",
                      border: sel ? "1px solid #171717" : "1px solid #E5E7EB",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { if (!sel) e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                    onMouseLeave={(e) => { if (!sel) e.currentTarget.style.backgroundColor = "#fff"; }}>
                    <Text ff="Inter" fz={12} fw={600} c={sel ? "#fff" : "#374151"}>{day}</Text>
                  </Box>
                );
              })}
            </Box>
            <Text ff="Inter" fz={11} c="#9CA3AF" mt={6}>{scheduleModalDaysOfMonth.length} day{scheduleModalDaysOfMonth.length !== 1 ? "s" : ""} selected</Text>
          </Box>
        )}

        {/* Save / Cancel */}
        <Flex gap={10} mt={8}>
          <Button color="gray" size="md" radius="md" fullWidth onClick={() => setScheduleModalOpen(false)}>
            Cancel
          </Button>
          <Button color="yellow" size="md" radius="md" fullWidth loading={scheduleModalSaving} onClick={handleScheduleModalSave}>
            Save Schedule
          </Button>
        </Flex>
      </Modal>
    </Box>
  );
};

export default ComplianceTab;
